import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import './interactive-data-table.scss';
import { Filters } from '../filters/filters';
import { Column, CrudConfig } from '../../../model/table';
import { FilterConfig } from '../../../model/filter';
import CustomButton from '../custom-button/custom-button';
import CustomIcon, { IconType } from '../custom-icon/custom-icon';
import { Table } from '../table/table';
import { EditorMode, TableMode } from '../../../utils/enums';
import { toCamelCase } from '../../../utils/string-normalization';
import { DataEditorModal } from '../edit-row-modal/edit-row-modal';
import { TableAction } from '../table-row/table-row';
import { TooltipDisableReason } from '../tooltip-disable-message/tooltip-disable-reason';
import { sortString } from '../../../utils/sort-util';

interface InteractiveDataTableProps<T> {
    filterConfig?: FilterConfig<T>;
    data: T[];
    columnsConfig: Column<T>[];
    crudConfig?: CrudConfig<T>;
    children?: ReactNode;
    emptyStateComp?: ReactNode;
    isInactiveRow?: (rowData: T) => boolean;
    isDraggable?: (rowData: T) => boolean;
    actions?: TableAction<T>[];
    moveRow?: (dragIndex: number, hoverIndex: number) => void;
    statusBadge?: ReactNode;
    dataHasLoaded: boolean;
    isLoading: boolean;
    defaultSortColumn?: keyof T;
    subTitle?: string;
}

export const InteractiveDataTable = <T,>(props: InteractiveDataTableProps<T>) => {
    const { filterConfig, data, columnsConfig, crudConfig,
        emptyStateComp, isInactiveRow, isDraggable, actions,
        moveRow, statusBadge, dataHasLoaded, isLoading, subTitle } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<EditorMode>();
    const [selectedRowData, setSelectedRowData] = useState<T | null>();
    const [filteredData, setFilteredData] = useState<T[] | undefined>(dataHasLoaded ? data : undefined);
    const [sortConfig, setSortConfig] = useState<{column: keyof T | null, direction: 'asc' | 'desc'}>({
        column: props.defaultSortColumn ?? null,
        direction: 'asc'
    });

    const getTableColumns = () => {
        return columnsConfig.filter(col => !col.isHidden);
    };

    const openModal = (rowData: T, modalMode: EditorMode) => {
        setSelectedRowData({ ...rowData });
        setModalMode(modalMode);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const save = async (editedRowData: T) => {
        if (modalMode === EditorMode.EDIT && selectedRowData) {
            await crudConfig?.updateCb?.(editedRowData, selectedRowData);
        }
        else if (modalMode === EditorMode.NEW) {
            await crudConfig?.saveCb?.(editedRowData);
        }
    };

    const setData = (data: T[]) => {
        if(dataHasLoaded) {
            setFilteredData(data);
        }
    };

    useEffect(() => {
        if (!filterConfig) {
            setData(data);
        }
    }, [data]);

    const isRowLimitReached = useMemo(() => !!crudConfig?.rowLimit && data.length >= crudConfig?.rowLimit, [data.length, crudConfig]);

    const getDisableReason = useMemo(() => {
        if (isRowLimitReached) return `You have reached the limit of ${crudConfig?.rowLimit} ${crudConfig?.entityName}s`;
        return crudConfig?.disableEditReason || '';
    }, [crudConfig?.rowLimit, crudConfig?.entityName, crudConfig?.disableEditReason, isRowLimitReached]);

    const sortedData = useMemo(() => {
        if (!filteredData || !sortConfig.column) return filteredData;
        const column = columnsConfig.find(col => col.accessor === sortConfig.column);
        if (column?.sortFn) {
            return [...filteredData].sort((a, b) => column.sortFn!(a, b, sortConfig.direction));
        }
        return [...filteredData].sort((a, b) => sortString(sortConfig.column!, sortConfig.direction, a, b));
    }, [filteredData, sortConfig, columnsConfig]);

    const handleSort = (column: keyof T) => {
        setSortConfig(prev => {
            if (prev.column === column) {
                return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { column, direction: 'asc' };
        });
    };

    const modalAction = modalMode === EditorMode.NEW ? 'Add' : 'Edit';
    const modalHeader = crudConfig?.addModalTitle && modalMode === EditorMode.NEW
        ? crudConfig.addModalTitle
        : toCamelCase(`${modalAction} ${crudConfig?.entityName}`);

    return <div className="table-with-filters-wrapper">
        <div className="table-with-filters-header">
            <div className={'sub-title'}>{subTitle}</div>
            {filterConfig && <Filters filterConfig={filterConfig}
                setData={setData}
                originalData={data}
            />}
            <div className="table-extra-header">
                {statusBadge}
                {crudConfig?.createNewText && crudConfig?.newRecord &&
                    <TooltipDisableReason overlayClassName="row-limit-tooltip" disableReason={getDisableReason}>
                        <div className="btn-add-wrapper">
                            <CustomButton
                                isDisabled={!crudConfig?.displayInlineActions || isRowLimitReached || !!crudConfig.disableEditReason}
                                prefixIcon={<CustomIcon type={IconType.PLUS} />}
                                label={crudConfig.createNewText}
                                className={`secondary-btn ${crudConfig.buttonClass}`}
                                onClick={() => openModal(crudConfig.newRecord as T, EditorMode.NEW)} />
                        </div>
                    </TooltipDisableReason>}
                {props.children}
            </div>
        </div>
        <div className="table-wrapper">
            <Table<T>
                columnsConfig={getTableColumns()}
                content={sortedData}
                crudConfig={crudConfig}
                mode={TableMode.SCROLL_X}
                isEmptyState={dataHasLoaded && !data.length}
                emptyStateComp={emptyStateComp}
                openRowToEdit={openModal}
                isInactive={isInactiveRow}
                isDraggable={isDraggable}
                actions={actions}
                moveRow={moveRow}
                isLoading={isLoading}
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        </div>
        <DataEditorModal
            isOpen={isModalOpen}
            closeModal={closeModal}
            rowData={selectedRowData}
            columnsConfig={columnsConfig}
            onSave={save}
            crudConfig={crudConfig}
            mode={modalMode}
            header={modalHeader}
            okBtnText={modalMode === EditorMode.NEW ? `Add  ${toCamelCase(crudConfig?.entityName)}` : 'Save'}
            duplicationValidator={crudConfig?.isDuplicated}
            data={data}
            InfoMsg={crudConfig?.InfoMsg}
        />
    </div>;
};